import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import DictionaryService from '../../../services/dictionary.service';

@Component({
	standalone: true,
	selector: 'toolkit-eek2-results',
	templateUrl: './eek2-results.component.html',
	styleUrl: './eek2-results.component.scss',
	imports: [CommonModule]
})
export class ToolkitEek2ResultsComponent implements OnChanges {
	private dictionaryService: DictionaryService = inject(DictionaryService);

	@Input() assessment: any = null;
	@Input() questions: any = null;
	@Input() answers: any[] = [];
	@Input() completedAt: any = null;
	@Input() showHistoryButton = false;
	@Output() historyRequested = new EventEmitter<void>();

	vm: any = {
		summaryCount: 0,
		gauges: [],
		riskTriggered: false,
		riskText: '',
		dateText: ''
	};

	private readonly gauge = { cx: 100, cy: 100, r: 78, w: 14 };
	private readonly needlePoints = '100,50 103.8,94 100,111 96.2,94';
	private readonly arcPath = this.buildArcPath();

	ngOnChanges(_changes: SimpleChanges) {
		this.rebuild();
	}

	get copy() {
		return {
			title: this.tr('Toolkit.EEK2.Results.Title'),
			disclaimerTitle: this.tr('Toolkit.EEK2.Results.Disclaimer.Title'),
			disclaimerBody: this.tr('Toolkit.EEK2.Results.Disclaimer.Body'),
			safetyTitle: this.tr('Toolkit.EEK2.Results.Safety.Title'),
			safetyBody: this.tr('Toolkit.EEK2.Results.Safety.Body'),
			completed: this.tr('Toolkit.EEK2.Results.Completed'),
			below: this.tr('Toolkit.EEK2.Results.Status.Below'),
			above: this.tr('Toolkit.EEK2.Results.Status.Above'),
			summaryGood: this.tr('Toolkit.EEK2.Results.Summary.Good'),
			summaryWarn: this.tr('Toolkit.EEK2.Results.Summary.Warn'),
			cutoff: this.tr('Toolkit.EEK2.Results.Cutoff'),
			viewHistory: this.tr('Toolkit.EEK2.Results.ViewHistory', this.isEstonianLocale() ? 'Vaata varasemaid hindamisi' : 'View past assessments')
		};
	}

	private rebuild() {
		const instrument = this.resolveInstrument();
		const subscales = Array.isArray(instrument?.subscales) ? instrument.subscales : [];
		const items = Array.isArray(instrument?.items) ? instrument.items.slice().sort((a, b) => (a?.order || 0) - (b?.order || 0)) : [];
		const answers = Array.isArray(this.answers) ? this.answers : [];

		this.vm.summaryCount = 0;
		this.vm.gauges = subscales.map((subscale) => {
			const scoredItems = items.filter((item) => this.belongsToSubscale(item, subscale));
			const score = scoredItems.reduce((sum, item) => {
				const value = Number(answers[(item?.order || 1) - 1]);
				return sum + (isFinite(value) ? value : 0);
			}, 0);
			const max = Number(subscale?.maxScore ?? scoredItems.length * 4);
			const cutoff = Number(subscale?.cutoff ?? 0);
			const ratio = max > 0 ? Math.max(0, Math.min(1, score / max)) : 0;
			const cutoffRatio = max > 0 ? Math.max(0, Math.min(1, cutoff / max)) : 0;
			const theta = Math.PI * (1 - cutoffRatio);
			const inner = {
				x: this.gauge.cx + (this.gauge.r - this.gauge.w) * Math.cos(theta),
				y: this.gauge.cy - (this.gauge.r - this.gauge.w) * Math.sin(theta)
			};
			const outer = {
				x: this.gauge.cx + (this.gauge.r + this.gauge.w / 2 + 4) * Math.cos(theta),
				y: this.gauge.cy - (this.gauge.r + this.gauge.w / 2 + 4) * Math.sin(theta)
			};
			const label = {
				x: this.gauge.cx + (this.gauge.r + 16) * Math.cos(theta),
				y: this.gauge.cy - (this.gauge.r + 16) * Math.sin(theta)
			};
			const above = score >= cutoff;
			if (above) this.vm.summaryCount++;
			return {
				key: subscale?.key || '',
				label: this.subscaleLabel(subscale),
				description: this.subscaleDescription(subscale),
				score,
				max,
				cutoff,
				above,
				statusLabel: above ? this.copy.above : this.copy.below,
				needleDeg: -90 + ratio * 180,
				cutoffInner: inner,
				cutoffOuter: outer,
				cutoffLabel: label
			};
		});

		const riskRules = instrument?.riskRules || null;
		if (riskRules?.itemId) {
			const riskItem = items.find((item) => item?.id === riskRules.itemId);
			const value = riskItem ? Number(answers[(riskItem?.order || 1) - 1]) : NaN;
			this.vm.riskTriggered = isFinite(value) && value >= Number(riskRules?.threshold ?? 1);
			this.vm.riskText = this.vm.riskTriggered
				? this.tr('Toolkit.EEK2.Results.Safety.Action')
				: '';
		} else {
			this.vm.riskTriggered = false;
			this.vm.riskText = '';
		}

		this.vm.dateText = this.completedAt ? this.formatDate(this.completedAt) : '';
	}

	private resolveInstrument(): any {
		const fromQuestions = this.questions?.instrument && typeof this.questions.instrument === 'object' ? this.questions.instrument : null;
		return fromQuestions || null;
	}

	private tr(key: string, fallback?: string): string {
		const translated = this.dictionaryService.translate(key);
		return translated && translated !== key ? translated : (fallback || key);
	}

	private subscaleLabel(subscale: any): string {
		const key = (subscale?.key || '').toString().toLowerCase();
		const fallback = subscale?.label || subscale?.labelEt || '';
		return this.tr(`Toolkit.EEK2.Results.Subscale.${key}.Label`, fallback);
	}

	private subscaleDescription(subscale: any): string {
		const key = (subscale?.key || '').toString().toLowerCase();
		const fallback = subscale?.description || subscale?.descriptionEt || '';
		return this.tr(`Toolkit.EEK2.Results.Subscale.${key}.Description`, fallback);
	}

	private belongsToSubscale(item: any, subscale: any): boolean {
		if (!item || !subscale) return false;
		if (item?.subscale_key && subscale?.key) return item.subscale_key === subscale.key;
		const order = Number(item?.order || 0);
		const range = Array.isArray(subscale?.itemRange) ? subscale.itemRange : null;
		return !!range && order >= Number(range[0]) && order <= Number(range[1]);
	}

	private buildArcPath(): string {
		const start = this.polar(0);
		const end = this.polar(1);
		return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${this.gauge.r} ${this.gauge.r} 0 0 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
	}

	private polar(frac: number) {
		const theta = Math.PI * (1 - frac);
		return {
			x: this.gauge.cx + this.gauge.r * Math.cos(theta),
			y: this.gauge.cy - this.gauge.r * Math.sin(theta)
		};
	}

	private isEstonianLocale(): boolean {
		const locale = (this.dictionaryService.getCurrentLocale() || 'en_GB').toString().toLowerCase();
		return locale.startsWith('et');
	}

	private formatDate(value: any): string {
		const date = new Date(value);
		if (!isFinite(date.getTime())) return '';
		return new Intl.DateTimeFormat(this.isEstonianLocale() ? 'et-EE' : 'en-GB', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		}).format(date);
	}

	get gaugeArcPath(): string {
		return this.arcPath;
	}

	get gaugeNeedlePoints(): string {
		return this.needlePoints;
	}

	onHistoryRequested() {
		this.historyRequested.emit();
	}
}
